import React, { useState, useEffect } from 'react';
import { 
  Thermometer, 
  Activity, 
  AlertTriangle, 
  CheckCircle, 
  TrendingUp, 
  TrendingDown,
  Cloud,
  Sun,
  Moon,
  Droplets,
  Wind,
  Eye,
  Settings,
  Download,
  Calendar,
  Bell,
  Wifi,
  WifiOff,
  Server,
  HardDrive,
  Cpu,
  BarChart3,
  LineChart,
  PieChart
} from 'lucide-react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend as ChartLegend } from 'chart.js';

ChartJS.register(ArcElement, ChartTooltip, ChartLegend);

// Mock data generator
const generateMockData = () => {
  const sensors = [
    { id: 'sensor1', name: 'Living Room', location: 'Main Floor', temp: 22.3, humidity: 45, status: 'online' },
    { id: 'sensor2', name: 'Bedroom', location: 'Second Floor', temp: 20.1, humidity: 52, status: 'online' },
    { id: 'sensor3', name: 'Kitchen', location: 'Main Floor', temp: 24.7, humidity: 38, status: 'online' },
    { id: 'sensor4', name: 'Basement', location: 'Lower Level', temp: 18.9, humidity: 65, status: 'offline' },
    { id: 'sensor5', name: 'Garage', location: 'Ground Level', temp: 15.2, humidity: 28, status: 'warning' },
    { id: 'sensor6', name: 'Attic', location: 'Upper Level', temp: 28.4, humidity: 23, status: 'online' },
  ];

  const historicalData = Array.from({ length: 24 }, (_, i) => ({
    time: `${23 - i}:00`,
    temperature: 20 + Math.random() * 8,
    humidity: 30 + Math.random() * 40,
  }));

  return { sensors, historicalData };
};

interface SensorCardProps {
  sensor: {
    id: string;
    name: string;
    location: string;
    temp: number;
    humidity: number;
    status: string;
  };
}

const SensorCard: React.FC<SensorCardProps> = ({ sensor }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'text-green-400';
      case 'warning': return 'text-yellow-400';
      case 'offline': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online': return <CheckCircle className="w-4 h-4" />;
      case 'warning': return <AlertTriangle className="w-4 h-4" />;
      case 'offline': return <WifiOff className="w-4 h-4" />;
      default: return <Wifi className="w-4 h-4" />;
    }
  };

  return (
    <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-gray-600 transition-all duration-200 hover:shadow-lg">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-white font-semibold text-lg">{sensor.name}</h3>
          <p className="text-gray-400 text-sm">{sensor.location}</p>
        </div>
        <div className={`flex items-center gap-2 ${getStatusColor(sensor.status)}`}>
          {getStatusIcon(sensor.status)}
          <span className="text-xs uppercase tracking-wide">{sensor.status}</span>
        </div>
      </div>
      
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Thermometer className="w-5 h-5 text-blue-400" />
            <span className="text-gray-300">Temperature</span>
          </div>
          <span className="text-2xl font-bold text-white">{sensor.temp}°C</span>
        </div>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Droplets className="w-5 h-5 text-cyan-400" />
            <span className="text-gray-300">Humidity</span>
          </div>
          <span className="text-xl font-semibold text-white">{sensor.humidity}%</span>
        </div>
      </div>
    </div>
  );
};

const MetricCard: React.FC<{ 
  title: string; 
  value: string; 
  change: string; 
  trend: 'up' | 'down';
  icon: React.ReactNode;
  color: string;
}> = ({ title, value, change, trend, icon, color }) => (
  <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
    <div className="flex items-center justify-between mb-4">
      <div className={`p-2 rounded-lg ${color}`}>
        {icon}
      </div>
      <div className={`flex items-center gap-1 ${trend === 'up' ? 'text-green-400' : 'text-red-400'}`}>
        {trend === 'up' ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
        <span className="text-sm">{change}</span>
      </div>
    </div>
    <h3 className="text-gray-400 text-sm mb-1">{title}</h3>
    <p className="text-white text-2xl font-bold">{value}</p>
  </div>
);

const ChartCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
    <h3 className="text-white font-semibold text-lg mb-4">{title}</h3>
    {children}
  </div>
);

const AnalyticsPage: React.FC = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [data] = useState(generateMockData());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const onlineSensors = data.sensors.filter(s => s.status === 'online').length;
  const avgTemp = data.sensors.reduce((acc, s) => acc + s.temp, 0) / data.sensors.length;
  const avgHumidity = data.sensors.reduce((acc, s) => acc + s.humidity, 0) / data.sensors.length;

  // Pie chart data for sensor status
  const sensorStatusCounts = {
    ok: data.sensors.filter(s => s.status === 'online').length,
    preAlarm: data.sensors.filter(s => s.status === 'warning').length,
    alarm: data.sensors.filter(s => s.status === 'offline').length,
  };

  const pieData = {
    labels: ['OK', 'Pre-Alarm', 'Alarm'],
    datasets: [
      {
        data: [sensorStatusCounts.ok, sensorStatusCounts.preAlarm, sensorStatusCounts.alarm],
        backgroundColor: [
          'rgba(34,197,94,0.7)',    // green-500
          'rgba(253,224,71,0.7)',   // yellow-400
          'rgba(239,68,68,0.7)',    // red-500
        ],
        borderColor: [
          'rgba(34,197,94,1)',
          'rgba(253,224,71,1)',
          'rgba(239,68,68,1)',
        ],
        borderWidth: 2,
      },
    ],
  };

  const pieOptions = {
    plugins: {
      legend: {
        display: true,
        position: 'bottom' as const,
        labels: {
          color: '#fff',
        },
      },
    },
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-2 bg-blue-600 rounded-lg">
              <Thermometer className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">TS1 Analytics</h1>
              <p className="text-gray-400">Temperature Monitoring System</p>
            </div>
          </div>
          
          <div className="flex items-center gap-6">
            <div className="text-right">
              <p className="text-sm text-gray-400">System Time</p>
              <p className="font-mono text-lg">{currentTime.toLocaleTimeString()}</p>
            </div>
            
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-green-400 text-sm">System Online</span>
            </div>
            
            <button className="p-2 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors">
              <Settings className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <div className="p-6 space-y-6">
        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <MetricCard
            title="Online Sensors"
            value={`${onlineSensors}/${data.sensors.length}`}
            change="+2.1%"
            trend="up"
            icon={<Wifi className="w-6 h-6" />}
            color="bg-green-500/20"
          />
          <MetricCard
            title="Avg Temperature"
            value={`${avgTemp.toFixed(1)}°C`}
            change="-1.3°C"
            trend="down"
            icon={<Thermometer className="w-6 h-6" />}
            color="bg-blue-500/20"
          />
          <MetricCard
            title="Avg Humidity"
            value={`${avgHumidity.toFixed(0)}%`}
            change="+3.2%"
            trend="up"
            icon={<Droplets className="w-6 h-6" />}
            color="bg-cyan-500/20"
          />
          
        </div>

        {/* Pie Chart for Sensor Status */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-gray-800 rounded-xl p-4 border border-gray-700 flex flex-col items-center justify-center">
            <h3 className="text-white font-semibold text-lg mb-2">Sensor Status Distribution</h3>
            <div className="w-full max-w-[180px] md:max-w-[200px]">
              <Pie data={pieData} options={pieOptions} />
            </div>
          </div>
          {/* The other half of the grid can be left for future charts or content */}
        </div>

        {/* Main Dashboard Grid */}
        <div className="lg:col-span-2">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Server className="w-5 h-5" />
            Sensor Network
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.sensors.map((sensor) => (
              <SensorCard key={sensor.id} sensor={sensor} />
            ))}
          </div>
        </div>

        {/* Side Panel */}
        <div className="space-y-6">
          {/* Weather Widget */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <h3 className="text-white font-semibold text-lg mb-4 flex items-center gap-2">
              <Cloud className="w-5 h-5" />
              Local Weather
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Sun className="w-8 h-8 text-yellow-400" />
                <div className="text-right">
                  <p className="text-2xl font-bold">18°C</p>
                  <p className="text-gray-400">Sunny</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Wind className="w-4 h-4 text-gray-400" />
                  <span>12 km/h</span>
                </div>
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-gray-400" />
                  <span>10 km</span>
                </div>
              </div>
            </div>
          </div>

          {/* System Status */}
         

          {/* Recent Alerts */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <h3 className="text-white font-semibold text-lg mb-4 flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Recent Alerts
            </h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5" />
                <div>
                  <p className="text-sm text-white">High temperature detected</p>
                  <p className="text-xs text-gray-400">Garage sensor - 2 min ago</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                <WifiOff className="w-4 h-4 text-red-400 mt-0.5" />
                <div>
                  <p className="text-sm text-white">Sensor offline</p>
                  <p className="text-xs text-gray-400">Basement sensor - 15 min ago</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                <CheckCircle className="w-4 h-4 text-green-400 mt-0.5" />
                <div>
                  <p className="text-sm text-white">System backup completed</p>
                  <p className="text-xs text-gray-400">Daily backup - 1 hour ago</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Temperature Trends (24h)">
          <div className="h-64 flex items-end justify-between gap-1">
            {data.historicalData.map((point, index) => (
              <div key={index} className="flex-1 flex flex-col items-center">
                <div 
                  className="bg-gradient-to-t from-blue-500 to-blue-300 w-full rounded-t"
                  style={{ height: `${(point.temperature / 30) * 100}%` }}
                ></div>
                <span className="text-xs text-gray-400 mt-1">{point.time}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between mt-4 text-sm text-gray-400">
            <span>0°C</span>
            <span>30°C</span>
          </div>
        </ChartCard>

        <ChartCard title="Humidity Levels (24h)">
          <div className="h-64 flex items-end justify-between gap-1">
            {data.historicalData.map((point, index) => (
              <div key={index} className="flex-1 flex flex-col items-center">
                <div 
                  className="bg-gradient-to-t from-cyan-500 to-cyan-300 w-full rounded-t"
                  style={{ height: `${point.humidity}%` }}
                ></div>
                <span className="text-xs text-gray-400 mt-1">{point.time}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between mt-4 text-sm text-gray-400">
            <span>0%</span>
            <span>100%</span>
          </div>
        </ChartCard>
      </div>

      {/* Quick Actions */}
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
        <h3 className="text-white font-semibold text-lg mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button className="flex items-center gap-2 p-3 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors">
            <Download className="w-4 h-4" />
            <span>Export Data</span>
          </button>
          <button className="flex items-center gap-2 p-3 bg-green-600 hover:bg-green-700 rounded-lg transition-colors">
            <BarChart3 className="w-4 h-4" />
            <span>Generate Report</span>
          </button>
          <button className="flex items-center gap-2 p-3 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors">
            <Calendar className="w-4 h-4" />
            <span>Schedule Task</span>
          </button>
          <button className="flex items-center gap-2 p-3 bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors">
            <Settings className="w-4 h-4" />
            <span>Configure</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage; 